/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React, { forwardRef, Ref } from 'react';
import { ICON_WARNING, ICON_CONTENT_COPY } from '../../../../Icons';
import { SdsIcon } from '../../../SdsIcon';
import { FormField } from '../../FormField';
import { TextFieldProps } from './TextField.props';
import { TEXTFIELD_TYPE_MULTIPLE, TEXTFIELD_TYPE_PASSWORD, TEXTFIELD_TYPE_TEXTAREA } from './TextField.types';

/**
 * SDS Text Field component.
 */
export const TextField = forwardRef((props: TextFieldProps, ref: Ref<HTMLInputElement>): JSX.Element => {
  const {
    classes,
    customChange,
    dataId,
    disabled,
    errorMessage,
    validationPattern,
    fieldLabel,
    hideLabel,
    icon,
    id,
    maxCharacters,
    name,
    placeholder,
    readOnly,
    rows = 7,
    showCopyIcon,
    type,
    value,
    ...rest
  } = props;

  const autoCompleteType = props.autoComplete || 'off';
  const wrapperClass = clsx(`sds-text-field`, {
    [`readonly`]: readOnly,
    [`sds-field-error`]: errorMessage,
    [`sds-field-icon`]: icon,
  });

  const handleCopy = async (): Promise<void> => {
    const input = document.getElementById(id) as HTMLInputElement;
    if (input) {
      try {
        await navigator.clipboard.writeText(input.value);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to copy: ', err);
      }
    }
  };

  const getInputField = (): JSX.Element => {
    const valueProp = customChange ? { value } : { defaultValue: value };

    switch (type) {
      case TEXTFIELD_TYPE_MULTIPLE:
        return (
          <textarea
            aria-label={fieldLabel}
            data-id={dataId}
            disabled={disabled || readOnly}
            id={id}
            maxLength={maxCharacters}
            name={name}
            onChange={customChange}
            onInput={(event): void => {
              const target = event.target as HTMLTextAreaElement;
              target.style.height = ``;
              target.style.height = `${target.scrollHeight}px`;
            }}
            placeholder={placeholder}
            ref={ref as React.Ref<HTMLTextAreaElement>}
            rows={rows}
            {...valueProp}
          />
        );
      case TEXTFIELD_TYPE_TEXTAREA:
        return (
          <textarea
            aria-label={fieldLabel}
            data-id={dataId}
            disabled={disabled || readOnly}
            id={id}
            maxLength={maxCharacters}
            name={name}
            onChange={customChange}
            placeholder={placeholder}
            ref={ref as React.Ref<HTMLTextAreaElement>}
            rows={rows}
            {...valueProp}
          />
        );
      default:
        return (
          <>
            {icon && (
              <div className="sds-text-field__icon">
                <SdsIcon svg={icon} />
              </div>
            )}
            <input
              aria-label={fieldLabel}
              autoComplete={autoCompleteType}
              data-id={dataId}
              disabled={disabled || readOnly}
              id={id}
              maxLength={maxCharacters}
              name={name}
              onChange={customChange}
              pattern={validationPattern}
              placeholder={placeholder}
              ref={ref}
              type={type === TEXTFIELD_TYPE_PASSWORD ? `password` : `text`}
              {...valueProp}
            />
            {errorMessage && (
              <div className="sds-field-error__icon">
                <SdsIcon svg={ICON_WARNING} />
              </div>
            )}
            {showCopyIcon && (
              <div className="sds-text-field__copy-icon">
                <button aria-label="Copy to clipboard" onClick={handleCopy} type="button">
                  <SdsIcon svg={ICON_CONTENT_COPY} />
                </button>
              </div>
            )}
          </>
        );
    }
  };

  // Configure field
  const formFieldProps = { ...props };
  formFieldProps.classes = clsx(`sds-form-input`, classes);

  return (
    <FormField {...formFieldProps}>
      <div className={wrapperClass} {...rest}>
        {getInputField()}
      </div>
    </FormField>
  );
});
